import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Text from '../../typography/Text';
import List from './components/List';
import Link from './components/Link';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Markdown');
const Markdown = /*#__PURE__*/memo(_ref => {
  let {
    classes: classesProp,
    className: classNameProp,
    components,
    content,
    linkCallback,
    overrides: overridesProp
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  const override = getOverrides(overridesProp, Markdown.overrides);
  const rootProps = useMemo(() => ({
    className: classNames(classes.root, classNameProp),
    children: content,
    components: {
      a: _ref2 => {
        let {
          node,
          children,
          ...props
        } = _ref2;
        return /*#__PURE__*/React.createElement(Link, _extends({
          variation: "primary",
          callback: linkCallback
        }, props, override.a), children);
      },
      p: _ref3 => {
        let {
          children
        } = _ref3;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "body",
          as: "p",
          className: classes.block
        }, override.p), children);
      },
      h1: _ref4 => {
        let {
          children
        } = _ref4;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h1",
          as: "h1",
          className: classes.block
        }, override.h1), children);
      },
      h2: _ref5 => {
        let {
          children
        } = _ref5;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h2",
          as: "h2",
          className: classes.block
        }, override.h2), children);
      },
      h3: _ref6 => {
        let {
          children
        } = _ref6;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h3",
          as: "h3",
          className: classes.block
        }, override.h3), children);
      },
      h4: _ref7 => {
        let {
          children
        } = _ref7;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h4",
          as: "h4",
          className: classes.block
        }, override.h4), children);
      },
      h5: _ref8 => {
        let {
          children
        } = _ref8;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h5",
          as: "h5",
          className: classes.block
        }, override.h5), children);
      },
      h6: _ref9 => {
        let {
          children
        } = _ref9;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h6",
          as: "h6",
          className: classes.block
        }, override.h6), children);
      },
      ul: _ref10 => {
        let {
          children
        } = _ref10;
        return /*#__PURE__*/React.createElement(List, _extends({
          items: children,
          bullet: "arrowRight"
        }, override.ul));
      },
      ol: _ref11 => {
        let {
          children
        } = _ref11;
        return /*#__PURE__*/React.createElement(List, _extends({
          items: children,
          type: "ordered"
        }, override.ol));
      },
      ...components
    },
    ...override.root
  }), [classNameProp, classes.block, classes.root, components, content, linkCallback, override.a, override.h1, override.h2, override.h3, override.h4, override.h5, override.h6, override.ol, override.p, override.root, override.ul]);
  return /*#__PURE__*/React.createElement(ReactMarkdown, rootProps);
});
Markdown.overrides = ['root', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol'];
process.env.NODE_ENV !== "production" ? Markdown.propTypes = {
  className: PropTypes.string,
  components: PropTypes.object,
  content: PropTypes.string.isRequired,
  linkCallback: PropTypes.func,
  overrides: PropTypes.object
} : void 0;
export default Markdown;